import React, { useMemo, useRef } from 'react';
import { Heart, IconFormIcons } from 'shared/assets/icons';
import { ActionTaskContextMenu } from 'shared/components/connected/action-task-context-menu';
import { Icon } from 'shared/components/ui/icon';
import { Tooltip } from 'shared/components/ui/tooltip';
import { tooltipDelay } from 'shared/constants';
import { useContextMenuState } from 'shared/hooks/use-context-menu-state';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { Task } from 'shared/types/task';
import { TaskActionLifeArea } from 'shared/types/task-action';

import * as Styled from './event.style';
import { EventCard } from './event-card';

export type LifeAreaEventProps = {
  task: Task;
  lifeAreas: LifeArea[];
  onClick?: (id: ID) => void;
  numberOfLines: number;
  timeText?: string;
  isPast?: boolean;
  completed?: boolean;
  onInteractionComplete?: () => void;
};

export const TaskLifeAreaEvent: React.FC<LifeAreaEventProps> = ({
  task,
  lifeAreas,
  onClick,
  numberOfLines,
  timeText,
  isPast,
  completed,
  onInteractionComplete,
}) => {
  const {
    open: openMenu,
    close: closeMenu,
    position: menuPosition,
  } = useContextMenuState();
  const containerRef = useRef<HTMLDivElement>(null);
  const _onClick = () => onClick?.(task.id);

  const lifeArea = useMemo(
    () =>
      lifeAreas.find(
        ({ id }) => id === (task.action as TaskActionLifeArea)?.meta?.id,
      ),
    [lifeAreas, task.action],
  );

  if (!lifeArea) {
    return null;
  }

  return (
    <>
      <EventCard
        ref={containerRef}
        title={lifeArea.name}
        color={lifeArea.color ?? undefined}
        timeText={timeText}
        completed={completed}
        isPast={isPast}
        numberOfLines={numberOfLines}
        onClick={_onClick}
        onContextMenu={openMenu}
        leading={
          <Styled.LeadingSlot
            $color={lifeArea.color ?? undefined}
            $past={isPast}
          >
            <Icon icon={IconFormIcons[lifeArea.iconName] ?? Heart} />
          </Styled.LeadingSlot>
        }
      />

      {!!menuPosition && (
        <ActionTaskContextMenu
          task={task}
          onClose={closeMenu}
          location={menuPosition}
          onAction={onInteractionComplete}
        />
      )}
      <Tooltip
        referenceElement={containerRef}
        label={lifeArea.name}
        delay={tooltipDelay}
      />
    </>
  );
};
